<template>
  <div ref="popup" class="popup" :class="{ collapsed: !expand }">
    <div class="heading">
      <div class="title"><slot name="title"></slot></div>
    </div>

    <ExpandTransition :show="expand" class="content" @click.stop>
      <slot name="content"></slot>
    </ExpandTransition>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { onClickOutside, useCssVar, useElementBounding, watchDebounced } from '@vueuse/core'
import ExpandTransition from '@/components/Transitions/ExpandTransition.vue'

const props = withDefaults(
  defineProps<{
    expand: boolean
  }>(),
  { expand: true },
)
const emits = defineEmits(['close'])

const popup = ref()
const { height } = useElementBounding(popup)

const expand = ref(props.expand)
const extraBottomMargin = useCssVar('--itinerary-page-extra-bottom-margin')

watchDebounced(height, () => (extraBottomMargin.value = `${height.value}px`), { debounce: 500 })

onClickOutside(popup, () => {
  if (expand.value) {
    emits('close')
  } else {
    expand.value = false
  }
})
</script>

<style lang="scss">
#itinerary-page {
  margin-bottom: var(--itinerary-page-extra-bottom-margin, 0);
}
</style>

<style lang="scss" scoped>
.popup {
  @include flex-column($spacing-4);
  position: fixed;
  bottom: 20px;
  left: 20px;
  right: 20px;
  z-index: 1000;
  background: $green-50;
  border-radius: $rounded-2xl;
  padding: $spacing-6;
  box-shadow: 0px -4px 12px 0px rgba(164, 164, 164, 0.1);

  @include desktop {
    bottom: 64px;
    width: 100%;
    max-width: 548px;
    left: calc((100% - 548px) / 2);
  }

  &.collapsed {
    @include clickable;
  }
}

.title {
  font-size: $text-xl;
  font-weight: $font-semibold;
  line-height: 26px;
}
</style>
